import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface ConnectionRequestProfile {
  id: string;
  full_name: string;
  username: string;
  profile_picture_url?: string;
  belt_rank?: string;
  academy?: string;
}

export function useConnectionRequests() {
  const { user } = useAuth();
  const [requests, setRequests] = useState<ConnectionRequestProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRequests = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    // Fetch the user_ids of the requesters using the function
    const { data, error } = await supabase.rpc('get_my_connection_requests');
    if (error) {
      setError('Error fetching connection requests');
      setLoading(false);
      return;
    }
    const ids = (data || []).map((r: any) => r.user_id).filter(Boolean);
    if (ids.length === 0) {
      setRequests([]);
      setLoading(false);
      return;
    }
    // Fetch the profiles of the requesters, including main academy
    const { data: profiles, error: profileError } = await supabase
      .from('profiles')
      .select('id, full_name, username, belt_rank, main_academy:main_academy_id(name)')
      .in('id', ids);
    if (profileError) {
      console.error('Error fetching requester profiles:', profileError);
      setError('Error fetching requester profiles.');
      setLoading(false);
      return;
    }
    // Map main academy to the academy field
    const requestsWithAcademy = (profiles || []).map((p: any) => ({
      ...p,
      academy: p.main_academy ? p.main_academy.name : undefined,
    }));
    setRequests(requestsWithAcademy);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const acceptRequest = useCallback(async (requesterId: string) => {
    await supabase.rpc('accept_connection_request', { requester_id: requesterId });
    await fetchRequests();
  }, [fetchRequests]);

  const declineRequest = useCallback(async (requesterId: string) => {
    await supabase.rpc('decline_connection_request', { requester_id: requesterId });
    await fetchRequests();
  }, [fetchRequests]);

  return { requests, loading, error, acceptRequest, declineRequest, refetch: fetchRequests };
} 